// eslint-disable-next-line no-restricted-imports
import { propOr, length, init, last, head, join, map, pipe } from 'ramda';
import React from 'react';
import styled, { css } from 'styled-components';
import { theUserIsInMexico } from '@peloton/internationalize';
import { slate1 } from '@engage/colors';
import { useReduxState } from '@engage/redux';
import type { ClassLanguage } from '@engage/settings';
import { getClassLanguagePreferences } from '@engage/settings';
import { spaces } from '@engage/styles';
import { label4Large } from '@engage/typography';
import { Classes, ClassesCopy } from '@members/copy';
import { Settings } from '@members/icons';
import { useLocale } from '@members/localization';
import { isLanguageEnabled } from '@members/settings';

type Props = {
  onOpen: () => void;
};

const StyledModalLink = styled.button`
  ${label4Large}
  display: flex;
  letter-spacing: normal;
  color: ${slate1};
  fill: ${slate1};
`;

const iconStyle = css`
  margin-left: auto;
  width: 20px;
  margin-right: ${spaces.xxSmall}px;
`;

const StyledSettingsIcon = styled(Settings)`
  ${iconStyle}
`;

const key: keyof ClassLanguage = 'localeName';
const getLanguageOrDefault = (language: ClassLanguage): string =>
  propOr('', key, language);

export const ClassLanguageSelect: React.FC<React.PropsWithChildren<Props>> = ({
  onOpen,
}) => {
  const locale = useLocale();
  const classLanguagePreferences = useReduxState(getClassLanguagePreferences);
  const enabledLanguages = classLanguagePreferences.filter(isLanguageEnabled);

  if (length(enabledLanguages) === 0) {
    return null;
  }
  const firstLanguage = pipe<ClassLanguage[], ClassLanguage, string>(
    head,
    getLanguageOrDefault,
  )(enabledLanguages);
  const lastLanguage = pipe<ClassLanguage[], ClassLanguage, string, string>(
    last,
    getLanguageOrDefault,
    l => (theUserIsInMexico(locale) ? l.toLowerCase() : l),
  )(enabledLanguages);
  const initLanguages = pipe<ClassLanguage[], ClassLanguage[], string[], string, string>(
    init,
    map(getLanguageOrDefault),
    join(', '),
    l => (theUserIsInMexico(locale) ? l?.[0]?.concat(l.slice(1).toLowerCase()) ?? l : l),
  )(enabledLanguages);

  return (
    <>
      <StyledModalLink data-test-id="filtersClassLanguageLink" onClick={onOpen}>
        <StyledSettingsIcon />
        <ClassesCopy
          id={Classes.ClassLanguageSettingLink}
          values={{
            numLanguages: length(enabledLanguages),
            firstLanguage,
            lastLanguage,
            initLanguages,
          }}
        />
      </StyledModalLink>
    </>
  );
};
